import type { GridItemChangeEvent } from "@tql/ui-components-react/kendo/grid";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import type {
	Commodity,
	CommodityUpdateBody,
} from "~/shared/schemas/commodity-schema.ts";

import { useUpdateSavedCommodity } from "./use-commodities.ts";

export type EditDescriptor = Record<string, boolean | string[]>;

type UseCommodityEditingProps<T extends Commodity = Commodity> = {
	data: T[];
	setData: React.Dispatch<React.SetStateAction<T[]>>;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	resetOn?: boolean;
};

export function useCommodityEditing<T extends Commodity = Commodity>({
	setData,
	setIsLoading,
	resetOn = false,
}: UseCommodityEditingProps<T>) {
	const updateSavedCommodityMutation = useUpdateSavedCommodity();
	const { showSuccess, showError } = useNotifications();

	const [edit, setEdit] = useState<EditDescriptor>({});
	const [originalData, setOriginalData] = useState<Record<string, T>>({});

	const isEditMode = useMemo(() => Object.values(edit).includes(true), [edit]);

	useEffect(() => {
		if (resetOn) {
			setEdit({});
			setOriginalData({});
		}
	}, [resetOn]);

	const enterEdit = useCallback((dataItem: T) => {
		setOriginalData((prev) => ({ ...prev, [dataItem.id]: { ...dataItem } }));
		setEdit({ [dataItem.id]: true });
	}, []);

	const handleSaveEdit = useCallback(
		(dataItem: T) => {
			setIsLoading(true);
			const originalItem = originalData[dataItem.id];

			setData((prev) =>
				prev.map((item) => (item.id === dataItem.id ? dataItem : item)),
			);
			setEdit((prev) => ({ ...prev, [dataItem.id]: false }));

			const unitTypeId =
				dataItem.unitType && typeof dataItem.unitType === "object"
					? Number(dataItem.unitType.id)
					: Number(dataItem.unitTypeId ?? 0);

			let freightClassId: number | null = null;
			if (dataItem.freightClass && typeof dataItem.freightClass === "object") {
				freightClassId = Number(dataItem.freightClass.id);
			} else if (dataItem.freightClassId) {
				freightClassId = Number(dataItem.freightClassId);
			}

			const updateData: CommodityUpdateBody = {
				id: dataItem.id,
				description: dataItem.description,
				unitTypeId,
				quantity: dataItem.quantity,
				weight: dataItem.weight,
				dimensionLength: dataItem.dimensionLength,
				dimensionWidth: dataItem.dimensionWidth,
				dimensionHeight: dataItem.dimensionHeight,
				freightClassId,
				isStackable: dataItem.isStackable,
				isHazmat: dataItem.isHazmat,
				nationalMotorFreightClassification: dataItem.nmfc ?? "",
			};

			const resolveDisplayName = () => {
				const trimmedDescription = dataItem.description?.trim();
				return trimmedDescription && trimmedDescription.length > 0
					? trimmedDescription
					: "Commodity";
			};

			updateSavedCommodityMutation.mutate(updateData, {
				onSuccess: () => {
					setIsLoading(false);
					setOriginalData((prev) => {
						const newOriginal = { ...prev };
						delete newOriginal[dataItem.id];
						return newOriginal;
					});
					showSuccess(`${resolveDisplayName()} updated in saved commodities.`);
				},
				onError: (err: Error) => {
					setIsLoading(false);
					if (originalItem) {
						setData((prevData) =>
							prevData.map((item) =>
								item.id === dataItem.id ? originalItem : item,
							),
						);
					}
					setOriginalData((prev) => {
						const newOriginal = { ...prev };
						delete newOriginal[dataItem.id];
						return newOriginal;
					});
					showError(
						err.message ??
							`Unable to update ${resolveDisplayName()}. Please try again shortly.`,
					);
				},
			});
		},
		[
			setData,
			setIsLoading,
			updateSavedCommodityMutation,
			showSuccess,
			showError,
			originalData,
		],
	);

	const handleCancelEdit = useCallback(
		(dataItem: T) => {
			const originalItem = originalData[dataItem.id];
			if (originalItem) {
				setData((prevData) =>
					prevData.map((item) =>
						item.id === dataItem.id ? originalItem : item,
					),
				);
			}
			setEdit((prev) => ({ ...prev, [dataItem.id]: false }));
			setOriginalData((prev) => {
				const newOriginal = { ...prev };
				delete newOriginal[dataItem.id];
				return newOriginal;
			});
		},
		[originalData, setData],
	);

	const itemChange = useCallback(
		(e: GridItemChangeEvent) => {
			const dataItem = e.dataItem as T;
			const field = e.field as unknown as keyof T;
			const value = e.value as unknown as T[keyof T];

			if (!field) return;

			setData((prevData) =>
				prevData.map((item) =>
					item.id === dataItem.id ? { ...item, [field]: value } : item,
				),
			);
		},
		[setData],
	);

	return {
		edit,
		isEditMode,
		enterEdit,
		handleSaveEdit,
		handleCancelEdit,
		itemChange,
	};
}
